import { Panel } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { NO_DATA } from '../lib/format.js';

interface Gate {
  key: string; value: string; enabled: boolean;
  meaning: string; changedVia: string; affectsSpend: boolean;
}
interface Credential {
  integration: string; mechanism: string; identity: string;
  secretHeld: boolean; detail: string;
}
interface Role {
  code: string; name: string; description: string;
  mayApprove: boolean; mayChangeBudget: boolean; mayViewSpend: boolean;
}
interface SettingsResponse {
  viewer: { name: string; username: string; roles: string[] };
  gates: Gate[];
  credentials: Credential[];
  access: { model: string; roles: Role[]; changedVia: string };
  tracking: { config_key: string; config_value: string; description: string; set_by: string; set_utc: string }[];
  commercialAssumptions: {
    assumption_code: string; display_name: string; value_low: number | null; value_high: number | null;
    unit: string; provenance: string; source: string; caveat: string | null;
  }[];
  precedence: {
    itUserScale: { source_code: string; display_name: string; precedence: number }[];
    staffCountScale: { source_code: string; display_name: string; precedence: number }[];
  };
  recommendationCategories: { code: string; name: string; affectsSpend: boolean }[];
  schema: { migrationsApplied: number; latest: { migration_id: string; applied_utc: string; applied_by: string }[] };
  syncs: { integration: string; last_run: string | null; succeeded: number; failed: number }[];
  intelligence: { modelId: string | null; promptVersion: string | null; provider: string | null; configured: boolean };
  generatedUtc: string;
}

const range = (lo: number | null, hi: number | null, unit: string): string => {
  if (lo === null) return NO_DATA;
  const n = (v: number) => new Intl.NumberFormat('en-GB').format(v);
  return hi === null || hi === lo ? `${n(lo)} ${unit}` : `${n(lo)} – ${n(hi)} ${unit}`;
};

export function Settings({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error } = useApi<SettingsResponse>('/api/settings', getToken);

  if (error) return <EmptyState title="Could not load settings" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading platform configuration.</EmptyState>;

  return (
    <div className="stack">
      <div className="panel">
        <h2>This screen is read-only</h2>
        <p>
          Several settings below decide whether the platform may spend money. A screen that can change
          them is a screen that can change them by accident, so each one states where it is actually
          changed — a route with an approval trail rather than a button. You are signed in as{' '}
          <strong>{data.viewer.name}</strong> with {data.viewer.roles.length > 0
            ? <>role {data.viewer.roles.join(', ')}</> : <>no role</>}.
        </p>
      </div>

      <Panel title="Operational gates" subtitle="What the platform is currently permitted to do.">
        <table className="data">
          <thead>
            <tr><th>Setting</th><th>Value</th><th>What it means</th><th>Changed via</th></tr>
          </thead>
          <tbody>
            {data.gates.map((g) => (
              <tr key={g.key}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                  {g.key}
                  {g.affectsSpend ? <div><span className="pill warn" style={{ marginTop: 4 }}>affects spend</span></div> : null}
                </td>
                <td>
                  <span className={g.enabled ? 'pill bad' : 'pill good'}>{g.value}</span>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{g.meaning}</td>
                <td style={{ fontSize: 12, color: 'var(--text-faint)' }}>{g.changedVia}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Credentials and identity" subtitle="How each integration authenticates. No secret value is ever returned by this screen.">
        <table className="data">
          <thead><tr><th>Integration</th><th>Mechanism</th><th>Identity</th><th>Holds a secret</th></tr></thead>
          <tbody>
            {data.credentials.map((c) => (
              <tr key={c.integration}>
                <td>{c.integration}</td>
                <td>{c.mechanism}<div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>{c.detail}</div></td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>{c.identity}</td>
                <td>{c.secretHeld
                  ? <span className="pill warn">Key Vault secret</span>
                  : <span className="pill good">Keyless</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Access model" subtitle={data.access.model}>
        <table className="data">
          <thead>
            <tr>
              <th>Role</th><th>Approve recommendations</th><th>Change budget</th><th>View spend</th>
            </tr>
          </thead>
          <tbody>
            {data.access.roles.map((r) => (
              <tr key={r.code}>
                <td>{r.name}<div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{r.code}</div></td>
                <td>{r.mayApprove ? <span className="pill warn">Yes</span> : <span className="pill">No</span>}</td>
                <td>{r.mayChangeBudget ? <span className="pill warn">Yes</span> : <span className="pill">No</span>}</td>
                <td>{r.mayViewSpend ? <span className="pill good">Yes</span> : <span className="pill">No</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-faint)' }}>{data.access.changedVia}</p>
      </Panel>

      <Panel
        title="Commercial assumptions"
        subtitle="These are business hypotheses supplied by the owner, not measurements. Their provenance is shown because a number used in a forecast must never be mistaken for one that was observed."
      >
        <table className="data">
          <thead><tr><th>Assumption</th><th className="num">Value</th><th>Provenance</th><th>Caveat</th></tr></thead>
          <tbody>
            {data.commercialAssumptions.map((a) => (
              <tr key={a.assumption_code}>
                <td>{a.display_name}</td>
                <td className="num">{range(a.value_low, a.value_high, a.unit)}</td>
                <td><span className="pill warn">{a.provenance.replace(/_/g, ' ')}</span></td>
                <td style={{ fontSize: 11, color: 'var(--text-faint)', maxWidth: 420 }}>{a.caveat ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Attribution and tracking" subtitle="Settings that decide what is captured and how it is attributed.">
        <table className="data">
          <thead><tr><th>Key</th><th>Value</th><th>Description</th><th>Set by</th></tr></thead>
          <tbody>
            {data.tracking.map((t) => (
              <tr key={t.config_key}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{t.config_key}</td>
                <td><span className="pill">{t.config_value}</span></td>
                <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 520 }}>{t.description}</td>
                <td style={{ fontSize: 11, color: 'var(--text-faint)' }}>
                  {t.set_by}<div>{t.set_utc?.slice(0, 10)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel
        title="Source precedence"
        subtitle="Two independent scales. A lower-confidence source never overwrites a higher one; the conflict is recorded for review instead."
      >
        <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div>
            <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>IT user count</h3>
            <table className="data">
              <tbody>
                {data.precedence.itUserScale.map((s) => (
                  <tr key={s.source_code}>
                    <td>{s.display_name}</td>
                    <td className="num">{s.precedence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>Staff count</h3>
            <table className="data">
              <tbody>
                {data.precedence.staffCountScale.map((s) => (
                  <tr key={s.source_code}>
                    <td>{s.display_name}</td>
                    <td className="num">{s.precedence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>
              Microsoft 365 licensing is deliberately absent here. It counts licensed users, which says
              nothing about how many people the business employs.
            </p>
          </div>
        </div>
      </Panel>

      <Panel title="Intelligence layer" subtitle="Recommendations and the AI Analyst.">
        {data.intelligence.configured ? (
          <table className="data">
            <tbody>
              <tr><td>Provider</td><td>{data.intelligence.provider}</td></tr>
              <tr><td>Model</td><td>{data.intelligence.modelId ?? NO_DATA}</td></tr>
              <tr><td>Prompt version</td><td>{data.intelligence.promptVersion ?? NO_DATA}</td></tr>
            </tbody>
          </table>
        ) : (
          <p style={{ color: 'var(--text-faint)' }}>
            No model is configured. Recommendations and the AI Analyst are not yet wired up, and this
            screen reports that rather than showing a default that would imply otherwise.
          </p>
        )}
        <p style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted)' }}>
          {data.recommendationCategories.filter((c) => c.affectsSpend).length} of{' '}
          {data.recommendationCategories.length} recommendation categories affect spend and will require
          explicit approval before execution:
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {data.recommendationCategories.map((c) => (
            <span key={c.code} className={c.affectsSpend ? 'pill warn' : 'pill'}>{c.name}</span>
          ))}
        </div>
      </Panel>

      <Panel title="Data pipeline" subtitle="Schema version and integration sync history.">
        <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div>
            <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>
              Schema — {data.schema.migrationsApplied} migrations applied
            </h3>
            <table className="data">
              <tbody>
                {data.schema.latest.map((m) => (
                  <tr key={m.migration_id}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{m.migration_id}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-faint)' }}>{m.applied_utc?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>Syncs</h3>
            <table className="data">
              <thead><tr><th>Integration</th><th className="num">OK</th><th className="num">Failed</th><th>Last run</th></tr></thead>
              <tbody>
                {data.syncs.map((s) => (
                  <tr key={s.integration}>
                    <td>{s.integration}</td>
                    <td className="num">{s.succeeded}</td>
                    <td className="num" style={{ color: s.failed > 0 ? 'var(--negative)' : undefined }}>{s.failed}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-faint)' }}>
                      {s.last_run ? s.last_run.replace('T', ' ').slice(0, 16) : NO_DATA}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
}
